<script setup lang="ts">
import type MDBPrismeModèlesSource from '@/types/prisme/modèlessource'
import type MDBPrismeModèleHistorique from '@/types/prisme/modèlehistorique'
import type MDBPrismeOignonBotrytis from '@/types/prisme/oignonbotrytis'
import type MDBPrismeOignonMildiou from '@/types/prisme/oignonmildiou'
import type { ECOption } from '@/plugins/echarts'
import { roundTo, notifyError } from '@/utils/helpers'
import { saveAs } from 'file-saver'



const theme = useTheme()
const storeU = useUtilisateurStore()
const sources = ref([] as MDBPrismeModèlesSource[])
const sourcesNom = computed(() => sources.value.map(s => s.nom))
const sourceSélectionnée = ref(null as MDBPrismeModèlesSource | null | undefined)
const sourceSélectionnéeNom = computed(() => sourceSélectionnée.value?.nom ?? 'Aucune')
const révision = ref('')
const isLoading = ref(false)
const voirMétéo = ref(false)
const voirAnnée = ref(false)
const nowMarkBotrytis = ref(0)
const nowMarkMildiou = ref(0)

declare interface MétéoData {
  dateS: string
  température: number
  humidité: number
  totalLiquide: number
  comblerSource: string
  comblerSourceId: string
  estPrévision: boolean
}
const météoData = ref([] as MétéoData[])

declare interface BotrytisData {
  dateS: string
  date: number
  lacy: number
  température: number
  humidité: number
}
const botrytisData = ref([] as BotrytisData[])

declare interface MildiouData {
  dateS: string
  date: number
  tMoy_08_20: number
  tMoy_20_06: number
  précip_01_06: number
  heuresRH_02_06: number
  risqueSporulation: number
}
const mildiouData = ref([] as MildiouData[])

onMounted(async () => { await getModèlesSource() })

watch(botrytisData, (newData) => {
  chartOptionsBotrytis.value.dataset = {
    source: newData,
    dimensions: ['date', 'humidité', 'température', 'lacy']
  };
  (chartOptionsBotrytis.value.series as any)[2].markLine.data[2].xAxis = nowMarkBotrytis.value;
}, { deep: true })

watch(mildiouData, (newData) => {
  chartOptionsMildiou.value.dataset = {
    source: newData,
    dimensions: ['date', 'risqueSporulation']
  };
  (chartOptionsMildiou.value.series as any)[0].markLine.data[0].xAxis = nowMarkMildiou.value;
}, { deep: true })

watch(() => storeU.siteSélectionné, async (_) => await getModèlesSource())
watch(() => storeU.annéeSélectionnée, async (_) => await getData())
watch(() => sourceSélectionnée.value, async (_) => await getData())
watch(() => voirAnnée.value, async (_) => await getData())
watch(() => voirMétéo.value, async (_) => setBotrytisMétéo())

// Revision check
const intervalId = setInterval(async () => {
  const siteCS = storeU.siteSélectionné.lettresDappel
  if (siteCS == undefined || siteCS == '' || !sourceSélectionnée.value) {
    return
  }

  const result = await apiProphet.get(`sites/${siteCS}/revisions/ogn_modeles`)
  if (révision.value != result.data) {
    getData()
  }
}, 3000)

const onSourceChange = async (source: string) => {
  sourceSélectionnée.value = sources.value.find(s => s.nom === source)
}

async function getModèlesSource() {
  sources.value = []
  sourceSélectionnée.value = null

  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    const result = await apiProphet.get(`prsm/${siteCS}/sources`)
    sources.value = result.data
  }
  catch (error) {
    notifyError(error)
  }

  if (sources.value.length > 0) {
    sourceSélectionnée.value = sources.value[0]
  }
}

async function getData() {
  isLoading.value = true
  météoData.value = []
  botrytisData.value = []
  mildiouData.value = []

  if (!sourceSélectionnée.value) {
    Notify.info('Aucune source de modèle sélectionnée')
    return
  }

  const siteCS = storeU.siteSélectionné.lettresDappel
  const result = await apiProphet.get(`sites/${siteCS}/revisions/ogn_modeles`)
  révision.value = result.data

  const dateStart = new Date()
  dateStart.setFullYear(storeU.annéeSélectionnée)
  if (voirAnnée.value) {
    dateStart.setMonth(4)
    dateStart.setDate(1)
  } else {
    dateStart.setDate(dateStart.getDate() - 14)
  }

  const dateBotrytis = new Date(dateStart)
  await getBotrytis(dateBotrytis)

  const dateMildiou = new Date(dateStart)
  await getMildiou(dateMildiou)

  const dateMétéo = new Date(dateStart)
  await getMétéo(dateMétéo)
  setBotrytisMétéo()

  isLoading.value = false
}

async function getMétéo(date: Date) {
  const siteCS = storeU.siteSélectionné.lettresDappel
  const find = sourceSélectionnée.value
  if (!find) {
    notifyError('Aucune source de modèle sélectionnée')
    return
  }

  for (;;) {
    const config = { params: { date: date, nombreJours: 7 } }
    const meteo = await apiProphet.get(`prsm/${siteCS}/historique/${find.id}/heure`, config)
    const meteos = meteo.data as MDBPrismeModèleHistorique[]
    meteos.forEach((d, _i) => {
      météoData.value.push({
        dateS: d.date,
        température: d.température,
        humidité: d.humidité,
        totalLiquide: d.totalLiquide,
        comblerSource: sources.value.find(s => s.id === d.comblerSourceId)?.nom || '',
        comblerSourceId: d.comblerSourceId,
        estPrévision: d.estPrévision
      })
    })

    // Increment date by 7 days
    date.setDate(date.getDate() + 7)
    if (date.getMonth() >= 9) {
      break
    }
  }
}

async function getMildiou(date: Date) {
  const siteCS = storeU.siteSélectionné.lettresDappel
  const find = sourceSélectionnée.value
  if (!find) {
    notifyError('Aucune source de modèle sélectionnée')
    return
  }

  try
  {
    const nowTime = new Date().getTime()
    let dateDiff = Number.MAX_SAFE_INTEGER
    for (;;) {
      const config = { params: { date: date, nombreJours: 31 } }
      const result = await apiProphet.get(`prsm/${siteCS}/ogn/mildiou/${find.id}`, config)
      mildiouData.value.push(...(result.data.sort((a: MDBPrismeOignonMildiou, b: MDBPrismeOignonMildiou) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime()}).map(
        (d: MDBPrismeOignonMildiou) => {
          const localDate = new Date(d.date)
          const checkDate = Math.abs(localDate.getTime() - nowTime)
          if (checkDate < dateDiff) {
            dateDiff = checkDate
            nowMarkMildiou.value = localDate.getTime()
          }
          return {
            dateS: d.date,
            date: localDate.getTime(),
            risqueSporulation: d.risqueSporulation,
            tMoy_08_20: d.tMoy_08_20,
            tMoy_20_06: d.tMoy_20_06,
            précip_01_06: d.précip_01_06,
            heuresRH_02_06: d.heuresRH_02_06
         }})
      ))

      // Increment date by 31 days
      date.setDate(date.getDate() + 31)
      if (date.getMonth() >= 9) {
        break
      }
    }
  }
  catch (error) {
    notifyError(error)
  }
}

function setBotrytisMétéo() {
  if (voirMétéo.value) {
    for (let i = 0; i < botrytisData.value.length; i++) {
      const brûlureData = botrytisData.value[i]
      const météo = météoData.value.find(m => m.dateS === brûlureData.dateS)
      if (météo) {
        brûlureData.température = météo.température
        brûlureData.humidité = météo.humidité
      }
    }
  } else {
    botrytisData.value.forEach(d => {
      d.température = 0
      d.humidité = 0
    })
  }
}

async function getBotrytis(date: Date) {
  const siteCS = storeU.siteSélectionné.lettresDappel
  const find = sourceSélectionnée.value
  if (!find) {
    notifyError('Aucune source de modèle sélectionnée')
    return
  }

  try
  {
    const nowTime = new Date().getTime()
    let dateDiff = Number.MAX_SAFE_INTEGER
    for (;;) {
      const config = { params: { date: date, nombreJours: 7 } }
      const result = await apiProphet.get(`prsm/${siteCS}/ogn/botrytis/${find.id}`, config)
      botrytisData.value.push(...(result.data.sort((a: MDBPrismeOignonBotrytis, b: MDBPrismeOignonBotrytis) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime()}).map(
        (d: MDBPrismeOignonBotrytis) => {
          const localDate = new Date(d.date)
          const checkDate = Math.abs(localDate.getTime() - nowTime)
          if (checkDate < dateDiff) {
            dateDiff = checkDate
            nowMarkBotrytis.value = localDate.getTime()
          }
          return {
            dateS: d.date,
            date: localDate.getTime(),
            lacy: roundTo(d.predictorLacy, 2),
            température: 0,
            humidité: 0,
         }})
      ))

      // Increment date by 7 days
      date.setDate(date.getDate() + 7)
      if (date.getMonth() >= 9) {
        break
      }
    }
  }
  catch (error) {
    notifyError(error)
  }
}

//https://echarts.apache.org/en/option.html#tooltip
const chartOptionsBotrytis = ref<ECOption>({
  backgroundColor: 'transparent',
  tooltip: { trigger: 'axis' },
  grid: { top: 20, left: '2%', right: '2%', bottom: '3%', containLabel: true, },
  xAxis: {
    type: 'time',
    axisLabel: {
      rotate: 60,
      formatter: function (value) {
        const date = new Date(value);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${month} / ${day}`;
      },
    },
    axisPointer: {
      label: {
        formatter: function(params) {
          const date = new Date(params.value);
          return date.toLocaleString();
        }
      }
    }
  },
  yAxis: { type: 'value', min: 0, max: 100 },
  series: [
    { name: 'Humidité', type: 'line', showSymbol: false, lineStyle: { width: 1 }, encode: { x: 'date', y: 'humidité' } },
    { name: 'Température', type: 'line', showSymbol: false, lineStyle: { width: 1 }, encode: { x: 'date', y: 'température' } },
    {
      name: 'Lacy', type: 'line', showSymbol: false,
      lineStyle: { width: 3, color: '#4CAF50' },
      itemStyle: { color: '#4CAF50' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0,    color: 'rgba(255, 82, 82, 0.65)' },
            { offset: 0.40, color: 'rgba(255, 82, 82, 0.40)' },
            { offset: 0.55, color: 'rgba(255, 82, 82, 0.08)' },
            { offset: 0.70, color: 'rgba(255, 82, 82, 0)' },
            { offset: 1,    color: 'rgba(255, 82, 82, 0)' }
          ]
        }
      },
      encode: { x: 'date', y: 'lacy' },
      markLine: {
        silent: true,
        lineStyle: { type: 'dashed' },
        data: [
          {
            name: 'Threshold 80', yAxis: 80, lineStyle: { color: '#FF5252' },
            label: { formatter: '80', position: 'end' }
          },
          {
            name: 'Threshold 50', yAxis: 50, lineStyle: { color: '#FFC107' },
            label: { formatter: '50', position: 'end' }
          },
          {
            name: "En ce moment",
            xAxis: nowMarkBotrytis.value,
            lineStyle: { color: '#4CAF50', type: 'solid', width: 2 },
            label: {
              formatter: "En ce moment", position: 'middle', rotate: 90,
              fontSize: 10, color: '#4CAF50', offset: [0, -5]
            }
          }
        ]
      }
    },
  ]
})

const chartOptionsMildiou = ref<ECOption>({
  backgroundColor: 'transparent',
  tooltip: { trigger: 'axis' },
  grid: { top: 20, left: '2%', right: '2%', bottom: '3%', containLabel: true, },
  xAxis: {
    type: 'time',
    axisLabel: {
      rotate: 60,
      formatter: function (value) {
        const date = new Date(value);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${month} / ${day}`;
      },
    },
    axisPointer: {
      label: {
        formatter: function(params) {
          const date = new Date(params.value);
          return date.toLocaleString();
        }
      }
    }
  },
  yAxis: { type: 'value', min: 0, max: 1 },
  series: [
    {
      name: 'Risque', type: 'bar', barWidth: '50%',
      encode: { x: 'date', y: 'risqueSporulation' },
      markLine: {
        silent: true,
        data: [
          {
            name: "En ce moment",
            xAxis: nowMarkBotrytis.value,
            lineStyle: { color: '#4CAF50', type: 'solid', width: 2 },
            label: {
              formatter: "En ce moment", position: 'middle', rotate: 90,
              fontSize: 10, color: '#4CAF50', offset: [0, -5]
            }
          }
        ]
      }
    },
  ]
})

const exportCSVBotrytis = () => {
  if (botrytisData.value.length === 0) {
    Notify.info('Aucune donnée à exporter')
    return
  }

  const headers = [
    { text: 'Date', value: 'date' },
    { text: 'Lacy', value: 'lacy' },
    { text: 'Temperature (C)', value: 'température' },
    { text: 'Humidite (%)', value: 'humidité' },
    { text: 'Precipitations (mm)', value: 'totalLiquide' },
    { text: 'Source ID', value: 'comblerSource' },
    { text: 'Est Prevision', value: 'estPrévision' }
  ]

  const items = botrytisData.value.map(item => {
    const météo = météoData.value.find(m => m.dateS === item.dateS)
    const dateObj = new Date(item.dateS)
    const pad = (n: number) => n.toString().padStart(2, '0')
    const dateS = `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())} ${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`
    return {
      date: dateS,
      lacy: item.lacy,
      température: roundTo(météo ? météo.température : item.température, 2),
      humidité: roundTo(météo ? météo.humidité : item.humidité, 1),
      totalLiquide: roundTo(météo ? météo.totalLiquide : 0, 3),
      comblerSource: (météo ? météo.comblerSource : ''),
      estPrévision: (météo ? météo.estPrévision : '')
    }
  })

  const csvContent = 'data:text/csv;charset=utf-8,' +
    headers.map(h => h.text).join(',') + '\n' +
    items.map(i => `${i.date},${i.lacy},${i.température},${i.humidité},${i.totalLiquide},${i.comblerSource},${i.estPrévision}`).join('\n')
  const encodedUri = encodeURI(csvContent)
  saveAs(encodedUri, `brûlure_feuille_${sourceSélectionnéeNom.value}_${storeU.annéeSélectionnée}.csv`)
}

const exportCSVMildiou = () => {
  if (mildiouData.value.length === 0) {
    Notify.info('Aucune donnée à exporter')
    return
  }

  const headers = [
    { text: 'Date', value: 'date' },
    { text: 'Risque', value: 'risque' },
    { text: 'T Moy 08-20', value: 'tMoy_08_20' },
    { text: 'T Moy 20-06', value: 'tMoy_20_06' },
    { text: 'Precip 01-06', value: 'précip_01_06' },
    { text: 'Heures RH >90% 02-06', value: 'heuresRH_02_06' }
  ]

  const items = mildiouData.value.map(item => {
    //const météo = météoData.value.find(m => m.dateS === item.dateS)
    return {
      date: new Date(item.date).toISOString().slice(0, 10),
      risque: item.risqueSporulation,
      tMoy_08_20: roundTo(item.tMoy_08_20, 2),
      tMoy_20_06: roundTo(item.tMoy_20_06, 2),
      précip_01_06: roundTo(item.précip_01_06, 2),
      heuresRH_02_06: roundTo(item.heuresRH_02_06, 2),
      //température: roundTo(météo ? météo.température : 0, 2),
      //humidité: roundTo(météo ? météo.humidité : 0, 1)
    }
  })

  const csvContent = 'data:text/csv;charset=utf-8,' +
    headers.map(h => h.text).join(',') + '\n' +
    items.map(i => `${i.date},${i.risque},${i.tMoy_08_20},${i.tMoy_20_06},${i.précip_01_06},${i.heuresRH_02_06}`).join('\n')
  const encodedUri = encodeURI(csvContent)
  saveAs(encodedUri, `mildiou_${sourceSélectionnéeNom.value}_${storeU.annéeSélectionnée}.csv`)
}

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-card class="mx-4 my-4" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Title + year -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Oignon: Modèles de prévisions</div>
            <div class="text-caption text-medium-emphasis">Brûlure de la feuille &amp; mildiou</div>
          </div>
          <v-divider vertical class="mx-2" style="height: 32px; align-self: center;"/>
          <YearSelector/>
        </div>

        <!-- Controls -->
        <div class="d-flex align-center flex-wrap" style="gap: 10px;">
          <v-checkbox v-model="voirAnnée" label="Voir toute l'année" class="ma-0 pa-0" hide-details density="compact"/>
          <v-select
            v-model="sourceSélectionnéeNom"
            style="min-width: 220px; max-width: 340px;"
            :items="sourcesNom"
            label="Source"
            variant="outlined"
            density="compact"
            rounded="lg"
            hide-details
            @update:model-value="onSourceChange"
          />
        </div>
      </div>
    </div>

    <v-divider/>

    <!-- Botrytis section -->
    <div class="section-header px-5 pt-4 pb-2">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
        <div class="d-flex align-center" style="gap: 10px;">
          <v-avatar color="success" variant="tonal" size="36" rounded="lg">
            <v-icon icon="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" size="20"/>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2;">Brûlure de la feuille</div>
            <div class="text-caption text-medium-emphasis">Botrytis squamosa</div>
          </div>
        </div>
        <div class="d-flex align-center" style="gap: 10px;">
          <v-checkbox v-model="voirMétéo" label="Voir données météo" class="ma-0 pa-0" hide-details density="compact"/>
          <v-btn
            v-if="!isLoading"
            color="primary" variant="tonal" rounded="pill" size="small"
            prepend-icon="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"
            @click="exportCSVBotrytis">
            Exportation CSV
          </v-btn>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4">
      <v-chart style="min-width: 200px; min-height: 320px;" :option="chartOptionsBotrytis" autoresize/>
    </div>

    <v-divider/>

    <!-- Mildiou section -->
    <div class="section-header px-5 pt-4 pb-2">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
        <div class="d-flex align-center" style="gap: 10px;">
          <v-avatar color="info" variant="tonal" size="36" rounded="lg">
            <v-icon icon="M12,20A6,6 0 0,1 6,14C6,10 12,3.25 12,3.25C12,3.25 18,10 18,14A6,6 0 0,1 12,20Z" size="20"/>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2;">Risque de mildiou de l'oignon</div>
            <div class="text-caption text-medium-emphasis">Peronospora destructor</div>
          </div>
        </div>
        <v-btn
          v-if="!isLoading"
          color="primary" variant="tonal" rounded="pill" size="small"
          prepend-icon="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"
          @click="exportCSVMildiou">
          Exportation CSV
        </v-btn>
      </div>
    </div>
    <div class="px-4 pb-4">
      <v-chart style="min-width: 200px; min-height: 220px;" :option="chartOptionsMildiou" autoresize/>
    </div>

  </v-card>
</template>

<style scoped>
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

.section-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"');
}
</style>
